'use client';
import { useState } from 'react';

export function PasswordChangeForm() {
    const [formData, setFormData] = useState({
        currentPassword: '',
        changedPassword: '',
        confirmPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{
        type: 'success' | 'error';
        text: string;
    } | null>(null);
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage(null);
        if (formData.changedPassword !== formData.confirmPassword) {
            setMessage({ type: 'error', text: 'New passwords do not match' });
            setIsLoading(false);
            return;
        }
        if (formData.changedPassword.length < 8) {
            setMessage({ type: 'error', text: 'New password must be at least 8 characters long' });
            setIsLoading(false);
            return;
        }
        try {
            const response = await fetch('/api/auth/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    currentPassword: formData.currentPassword,
                    newPassword: formData.changedPassword,
                }),
            });
            const data = (await response.json()) as {
                error?: string;
            };
            if (response.ok) {
                setMessage({ type: 'success', text: 'Password changed successfully' });
                setFormData({ currentPassword: '', changedPassword: '', confirmPassword: '' });
            }
            else {
                setMessage({ type: 'error', text: data.error || 'Failed to change password' });
            }
        }
        catch {
            setMessage({ type: 'error', text: 'An error occurred while changing password' });
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };
    return (<form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-6">
      <div>
        <label htmlFor="currentPassword" className="block text-sm font-medium text-foreground-default">
          Current Password
        </label>
        <div className="mt-1">
          <input id="currentPassword" name="currentPassword" type="password" required value={formData.currentPassword} onChange={handleChange} className="block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary-default focus:border-primary-default sm:text-sm bg-background-secondary text-foreground-default"/>
        </div>
      </div>

      <div>
        <label htmlFor="newPassword" className="block text-sm font-medium text-foreground-default">
          New Password
        </label>
        <div className="mt-1">
          <input id="newPassword" name="changedPassword" type="password" required minLength={8} value={formData.changedPassword} onChange={handleChange} className="block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary-default focus:border-primary-default sm:text-sm bg-background-secondary text-foreground-default"/>
        </div>
        <p className="mt-1 text-sm text-foreground-muted">Must be at least 8 characters long</p>
      </div>

      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground-default">
          Confirm New Password
        </label>
        <div className="mt-1">
          <input id="confirmPassword" name="confirmPassword" type="password" required value={formData.confirmPassword} onChange={handleChange} className="block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary-default focus:border-primary-default sm:text-sm bg-background-secondary text-foreground-default"/>
        </div>
      </div>

      {message && (<div className={`rounded-md p-4 ${message.type === 'success'
                ? 'bg-success-default/10 border border-success-default/30'
                : 'bg-danger-default/10 border border-danger-default/30'}`}>
          <p className={`text-sm ${message.type === 'success' ? 'text-success-default' : 'text-danger-default'}`}>
            {message.text}
          </p>
        </div>)}

      <div className="flex justify-end">
        <button type="submit" disabled={isLoading} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed">
          {isLoading ? 'Changing...' : 'Change Password'}
        </button>
      </div>
    </form>);
}
